import { AlertTriangle, Info } from "lucide-react";

// Free-text boxes that drive the printed length of the CMS-485
export const CMS485_TEXT_FIELDS = {
  principal_diagnosis: "Principal Diagnosis",
  secondary_diagnosis: "Other Diagnoses",
  other_diagnoses: "Other Pertinent Diagnoses",
  surgical_procedures: "Surgical Procedures",
  dme_supplies: "DME & Supplies",
  safety_measures: "Safety Measures",
  nutritional_requirements: "Nutritional Requirements",
  allergies: "Allergies",
  orders_for_discipline: "Orders for Discipline",
  pcw_mots: "MOT's",
  pcw_interventions_parameters: "Interventions/Parameters",
  goals: "Goals/Discharge Plans",
  prognosis: "Prognosis",
};

// Roughly what fits at full size on one page (calibrated against the PDF builder's shrink steps)
const FULL_SIZE_CHARS = 2600;
const SMALL_PRINT_CHARS = 5200;
export const FIELD_SOFT_LIMIT = 450;

export function cms485LengthStatus(form) {
  const perField = Object.keys(CMS485_TEXT_FIELDS).map((k) => ({ key: k, label: CMS485_TEXT_FIELDS[k], len: String(form?.[k] || "").length }));
  const total = perField.reduce((n, f) => n + f.len, 0);
  const longFields = perField.filter((f) => f.len > FIELD_SOFT_LIMIT).sort((a, b) => b.len - a.len);
  const level = total > SMALL_PRINT_CHARS ? "severe" : total > FULL_SIZE_CHARS ? "shrink" : "ok";
  return { total, level, longFields, pct: Math.min(100, Math.round((total / FULL_SIZE_CHARS) * 100)) };
}

export function CMS485LengthNotice({ form }) {
  const { total, level, longFields, pct } = cms485LengthStatus(form);
  if (level === "ok" && longFields.length === 0) return null;
  const severe = level === "severe";
  const shrink = level !== "ok";
  return (
    <div
      className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${severe ? "bg-rose-50 border-rose-200 text-rose-900" : shrink ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-sky-50 border-sky-200 text-sky-900"}`}
      data-testid="cms485-length-notice"
      data-level={level}
    >
      {shrink ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> : <Info className="w-4 h-4 mt-0.5 shrink-0" />}
      <div className="space-y-1">
        <p className="font-medium">
          {severe
            ? "This 485 will print in very small type to stay on one sheet."
            : shrink
              ? "This 485 will shrink to fit on one sheet."
              : "Some boxes are getting long."}
          <span className="font-normal text-xs ml-2 opacity-80">{total.toLocaleString()} characters · {pct}% of full-size space</span>
        </p>
        {longFields.length > 0 && (
          <p className="text-xs">
            Longest: {longFields.slice(0, 3).map((f) => `${f.label} (${f.len})`).join(", ")}. Trimming these keeps the print readable.
          </p>
        )}
      </div>
    </div>
  );
}

export function FieldLengthHint({ value }) {
  const len = String(value || "").length;
  if (len <= FIELD_SOFT_LIMIT) return null;
  return (
    <p className="text-[11px] text-amber-700" data-testid="cms485-field-length-hint">
      {len} characters — long entries make the printed 485 shrink to fit
    </p>
  );
}
